import { Money, isValidMoney } from '@betterment/core';
import { FlowValues, StepProps } from '@betterment/js-runtime';

export interface CoordinatedAccount {
  id: string;
  name: string;
  amount: Money;
  shares: number;
  is401kAccount: boolean;
}

export const OrderUnit = {
  Dollars: 'dollars',
  Shares: 'shares',
} as const;

export type OrderUnit = (typeof OrderUnit)[keyof typeof OrderUnit];

export type MoneyChoice = {
  amount: Money;
  unit: typeof OrderUnit.Dollars;
};

export type SharesChoice = {
  amount: number;
  unit: typeof OrderUnit.Shares;
};

export type AmountChoice = MoneyChoice | SharesChoice;

export function toAmountChoice(unit: OrderUnit, amount: Money | number): AmountChoice {
  if (unit === OrderUnit.Dollars && isValidMoney(amount)) {
    return { amount, unit };
  }

  if (unit === OrderUnit.Shares && typeof amount === 'number') {
    return { amount, unit };
  }

  throw new Error(`Invalid amount/unit combination: ${unit} with ${typeof amount}`);
}

export interface SellOrderFormState {
  amount?: number | Money;
  unit: OrderUnit;
  assetName: string;
  assetTicker: string;
  pricePerShare: Money;
  coordinatedAccount: CoordinatedAccount;
  financialSecurityId: string;
  sellAll: boolean;
}

type SellOrderBase = Omit<SellOrderFormState, 'amount' | 'unit'>;

export type ValidatedSellOrder = SellOrderBase & AmountChoice;
export type ValidatedSellAllWarningInput = SellOrderBase & MoneyChoice;
export type ValidatedSellAllWarningOutput = SellOrderBase & SharesChoice;
export type ValidatedSellAllUnion = ValidatedSellAllWarningInput | ValidatedSellAllWarningOutput;

export type SellOrderFlowValues = FlowValues<SellOrderFormState>;

export type SellOrderStepProps = StepProps<SellOrderFlowValues>;
export type ReviewOrderStepProps = StepProps<FlowValues<ValidatedSellOrder>>;
export type SellAllWarningStepProps = StepProps<FlowValues<ValidatedSellAllUnion>>;
export type FourOhOneKBlockedErrorPageStepProps = StepProps<FlowValues<{}>>;
export type PendingActionErrorPageStepProps = StepProps<FlowValues<{}>>;

export function toValidatedSellOrder(state: SellOrderFormState): ValidatedSellOrder {
  const { amount, unit, coordinatedAccount, sellAll } = state;

  if (!coordinatedAccount) {
    throw new Error('Coordinated account is required');
  }
  if (!sellAll && !amount) {
    throw new Error('Amount is required');
  }

  const amountChoice = sellAll
    ? toAmountChoice(OrderUnit.Shares, coordinatedAccount.shares)
    : toAmountChoice(unit, amount!);

  return {
    ...state,
    ...amountChoice,
  };
}

export function toSellAllWarning(state: SellOrderFormState): ValidatedSellAllWarningInput {
  const { amount, unit, sellAll } = state;

  if (!sellAll) {
    throw new Error('sellAll is not true');
  }

  if (unit === OrderUnit.Dollars && isValidMoney(amount)) {
    return { ...state, amount, unit };
  }

  throw new Error(`Invalid amount/unit combination: ${unit} with ${typeof amount}`);
}

export function toReviewOrderProps(stepProps: SellOrderStepProps): ReviewOrderStepProps {
  return {
    ...stepProps,
    flowState: {
      ...stepProps.flowState,
      values: toValidatedSellOrder(stepProps.flowState.values),
    },
  };
}
export function toSellAllWarningProps(stepProps: SellOrderStepProps): SellAllWarningStepProps {
  return {
    ...stepProps,
    flowState: {
      ...stepProps.flowState,
      values: toSellAllWarning(stepProps.flowState.values),
    },
  };
}

export function toSubmittableOrderValue({ unit, amount }: AmountChoice): { shares: number } | { amount: Money } {
  if (unit === OrderUnit.Shares) {
    return { shares: amount };
  }
  return { amount };
}

export function toValidatedSellAllWarningOutput(values: ValidatedSellAllUnion): ValidatedSellAllWarningOutput {
  if (values.sellAll) {
    return {
      ...values,
      unit: OrderUnit.Shares,
      amount: values.coordinatedAccount.shares,
    };
  }
  throw new Error('sellAll is not true');
}
